import { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import api from "../Axios/api";
import Loader from "../common/Loader";
import { Eye, X, FileUp } from "lucide-react";
import * as XLSX from "xlsx";
const LotSummaryRecord = () => {
    const [vouchers, setVouchers] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedFactory, setSelectedFactory] = useState("all");
    const [month, setMonth] = useState("");

    const [showDetailModal, setShowDetailModal] = useState(false);
    const [lotDetails, setLotDetails] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState("");
    const [selectedLot, setSelectedLot] = useState(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchData = async () => {
        try {
            setLoading(true);
            setError("");

            const response = await api.get(
                `/inventory/lotSummaryRecord?month=${month}`
            );

            const sortedData = (response.data?.data || []).sort(
                (a, b) => new Date(a.lotDate) - new Date(b.lotDate)
            );

            setVouchers(sortedData);

        } catch (err) {
            console.error("Error fetching lot summary records:", err);

            setError(
                err.response?.data?.message ||
                "Failed to load lot summary records."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (month) {
            fetchData();
        }
    }, [month]);

    useEffect(() => {
        const now = new Date();
        const year = now.getFullYear();
        const monthNum = String(now.getMonth() + 1).padStart(2, "0");
        const currentMonth = `${year}-${monthNum}`;
        setMonth(currentMonth);
    }, []);

    const formatDate = (date) => {
        if (!date) return "-";

        const parsedDate = new Date(date);

        if (Number.isNaN(parsedDate.getTime())) return "-";

        return parsedDate.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            timeZone: "UTC",
        });
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(Number(amount || 0));
    };

    const factories = useMemo(() => {
        return [
            ...new Set(
                vouchers
                    .map((item) => item.factory)
                    .filter(Boolean)
            ),
        ].sort();
    }, [vouchers]);

    // Search and Factory Filter
    const filteredVouchers = useMemo(() => {
        const searchText = search.trim().toLowerCase();

        return vouchers.filter((item) => {
            const matchesSearch = [
                item.lotNo,
                item.document_no,
                item.factory,
                item.itemName,
                item.party,
                item.referenceType,
                item.tech_desc,
            ].some((value) =>
                String(value || "")
                    .toLowerCase()
                    .includes(searchText)
            );

            const matchesFactory =
                selectedFactory === "all" ||
                item.factory === selectedFactory;

            return matchesSearch && matchesFactory;
        });
    }, [vouchers, search, selectedFactory]);

    // Table Columns
    const columns = [
        {
            name: "S.No.",
            cell: (row, index) =>
                filteredVouchers.findIndex(
                    (item) => item._id === row._id
                ) + 1,
            width: "75px",
        },
        // {
        //     name: "Lot No.",
        //     selector: (row) => row.lotNo || "-",
        //     sortable: true,
        //     minWidth: "155px",
        //     cell: (row) => (
        //         <span className="font-semibold text-blue-700">
        //             {row.lotNo || "-"}
        //         </span>
        //     ),
        // },
        {
            name: "Doc No.",
            selector: (row) => row.document_no || "-",
            sortable: true,
            minWidth: "140px",
            cell: (row) => row.document_no || "-",
        },
        {
            name: "Doc Date",
            selector: (row) => row.lotDate || "",
            sortable: true,
            minWidth: "120px",
            cell: (row) => formatDate(row.lotDate),
        },
        {
            name: "Reference Type",
            selector: (row) => row.referenceType || "-",
            sortable: true,
            minWidth: "135px",
            cell: (row) => {
                const isOpening = row.referenceType === "OPENING";

                return (
                    <span
                        className={`rounded-full px-2.5 py-1 text-xs font-medium ${isOpening
                            ? "bg-purple-50 text-purple-700"
                            : "bg-green-50 text-green-700"
                            }`}
                    >
                        {row.referenceType || "-"}
                    </span>
                );
            },
        },

        {
            name: "Factory",
            selector: (row) => row.factory || "-",
            sortable: true,
            minWidth: "200px",
            wrap: true,
            cell: (row) => (
                <span className="rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700">
                    {row.factory || "-"}
                </span>
            ),
        },
        {
            name: "Item",
            selector: (row) => row.itemName || "-",
            sortable: true,
            minWidth: "200px",
            wrap: true,
        },
        {
            name: "Party",
            selector: (row) => row.party || "-",
            sortable: true,
            minWidth: "120px",
            cell: (row) => row.party || "-",
        },
        {
            name: "Technical Desc",
            selector: (row) => row.tech_desc || "-",
            sortable: true,
            minWidth: "160px",
            wrap: true,
            cell: (row) => row.tech_desc || "-",
        },
        {
            name: "In Qty",
            selector: (row) => Number(row.inQty || 0),
            sortable: true,
            right: true,
            minWidth: "100px",
            cell: (row) => (
                <span className="font-medium text-green-700">
                    {Number(row.inQty || 0).toLocaleString("en-IN")}
                </span>
            ),
        },
        {
            name: "Out Qty",
            selector: (row) => Number(row.outQty || 0),
            sortable: true,
            right: true,
            minWidth: "100px",
            cell: (row) => (
                <span className="font-medium text-red-600">
                    {Number(row.outQty || 0).toLocaleString("en-IN")}
                </span>
            ),
        },
        {
            name: "Balance Qty",
            selector: (row) => Number(row.balanceQty || 0),
            sortable: true,
            right: true,
            minWidth: "120px",
            cell: (row) => (
                <span
                    className={`rounded-md px-2 py-1 font-semibold ${Number(row.balanceQty || 0) > 0
                        ? "bg-blue-50 text-blue-700"
                        : "bg-gray-100 text-gray-600"
                        }`}
                >
                    {Number(row.balanceQty || 0).toLocaleString("en-IN")}
                </span>
            ),
        },
        {
            name: "Basic Rate",
            selector: (row) => Number(row.basic_rate || 0),
            sortable: true,
            right: true,
            minWidth: "125px",
            cell: (row) => formatAmount(row.basic_rate),
        },
        {
            name: "Pending Days",
            selector: (row) => row.lotDate || "",
            sortable: true,
            minWidth: "130px",
            cell: (row) => {
                if (!row.lotDate) return "";

                const lotDate = new Date(row.lotDate);
                const currentDate = new Date();

                // Ignore the time portion
                lotDate.setHours(0, 0, 0, 0);
                currentDate.setHours(0, 0, 0, 0);

                const diffTime = currentDate - lotDate;
                const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

                return `${diffDays} days`;
            },
        },

        {
            name: "Action",
            width: "100px",
            center: true,
            cell: (row) => (
                <div className="flex items-center justify-center gap-2">
                    <button
                        type="button"
                        onClick={() => handleView(row)}
                        title="View Lot Details"
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-cyan-200 bg-cyan-50 text-cyan-600 transition-all duration-200 hover:border-cyan-500 hover:bg-cyan-500 hover:text-white"
                    >
                        <Eye size={16} />
                    </button>
                </div>
            ),
        },
    ];

    const exportToExcel = (data) => {
        if (!data || data.length === 0) {
            alert("No data available to export");
            return;
        }

        // Format API data for Excel
        const excelData = data.map((item, index) => ({
            "S.No.": index + 1,
            "Document No.": item.document_no || "-",
            "Document Date": item.lotDate
                ? new Date(item.lotDate).toLocaleDateString("en-IN")
                : "-",
            "Reference Type": item.referenceType || "-",
            "Factory": item.factory || "-",
            "Item": item.itemName || "-",
            "Party": item.party || "-",
            "In Qty": Number(item.inQty || 0),
            "Out Qty": Number(item.outQty || 0),
            "Balance Qty": Number(item.balanceQty || 0),
            "Basic Rate": Number(item.basic_rate || 0),
            "Pending Days": item.lotDate
                ? Math.floor(
                    (new Date().setHours(0, 0, 0, 0) -
                        new Date(item.lotDate).setHours(0, 0, 0, 0)) /
                    (1000 * 60 * 60 * 24)
                )
                : 0,
            "Technical Description": item.tech_desc || "-",
        }));

        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Set column widths
        worksheet["!cols"] = [
            { wch: 8 },
            { wch: 25 },
            { wch: 20 },
            { wch: 20 },
            { wch: 15 },
            { wch: 25 },
            { wch: 18 },
            { wch: 15 },
            { wch: 15 },
            { wch: 18 },
            { wch: 15 },
            { wch: 15 },
            { wch: 30 },
        ];

        // Create workbook
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Lot Summary"
        );

        // Download Excel
        XLSX.writeFile(
            workbook,
            `Lot_Summary_${new Date().toISOString().slice(0, 10)}.xlsx`
        );
    };
    const handleView = async (row) => {
        setSelectedLot(row);
        setShowDetailModal(true);
        setLotDetails([]);
        setDetailError("");

        try {
            setDetailLoading(true);

            const response = await api.get(
                `/inventory/lotDetailRecord/${row._id}`
            );

            setLotDetails(response.data?.data || []);
        } catch (err) {
            console.error("Error fetching lot details:", err);

            setDetailError(
                err.response?.data?.message ||
                "Failed to load lot detail records."
            );
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetailModal = () => {
        setShowDetailModal(false);
        setSelectedLot(null);
        setLotDetails([]);
        setDetailError("");
    };
    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "#f9fafb",
                borderBottom: "1px solid #e5e7eb",
                minHeight: "52px",
            },
        },
        headCells: {
            style: {
                color: "#4b5563",
                fontSize: "12px",
                fontWeight: "600",
            },
        },
        rows: {
            style: {
                minHeight: "55px",
                fontSize: "13px",
                color: "#374151",
            },
            highlightOnHoverStyle: {
                backgroundColor: "#f9fafb",
                transition: "background-color 0.2s",
            },
        },
        pagination: {
            style: {
                borderTop: "1px solid #e5e7eb",
                fontSize: "13px",
            },
        },
    };

    return (
        <div className="p-4 md:p-6">
            <div className="mb-4 flex flex-col gap-3 rounded-lg bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">
                        Lot Summary Record
                    </h1>

                </div>

                {/* Month Filter */}
                <div className="flex flex-col gap-1">

                    <input
                        id="lot-month"
                        type="month"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                </div>
            </div>

            <div className="mb-4 flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm md:flex-row md:items-center">
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search lot, item, document, party..."
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 md:w-80"
                />

                <select
                    value={selectedFactory}
                    onChange={(e) => setSelectedFactory(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 md:w-60"
                >
                    <option value="all">All Factories</option>

                    {factories.map((factory) => (
                        <option key={factory} value={factory}>
                            {factory}
                        </option>
                    ))}
                </select>
                <button
                    onClick={() => exportToExcel(vouchers)}
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 active:scale-95 text-white px-5 py-2.5 rounded-lg font-medium text-sm shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                >
                    <FileUp size={18} strokeWidth={2} />
                    Export
                </button>
            </div>

            {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                    {error}
                </div>
            )}

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                {loading ? (
                    <Loader />
                ) : (
                    <DataTable
                        columns={columns}
                        data={filteredVouchers}
                        keyField="_id"
                        customStyles={customStyles}
                        pagination
                        paginationPerPage={10}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        highlightOnHover
                        responsive
                        striped
                        persistTableHead
                        noDataComponent={
                            <div className="py-10 text-sm text-gray-500">
                                {error
                                    ? "Unable to load lot records."
                                    : "No lot records found."}
                            </div>
                        }
                    />
                )}
            </div>



            {/* Lot Detail Modal */}
            {showDetailModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-6"
                    onClick={closeDetailModal}
                >
                    <div
                        className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-xl bg-white shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-5 py-4">
                            <div>
                                <h2 className="text-lg font-bold text-gray-800">
                                    Lot Detail Records
                                </h2>

                                <p className="mt-1 text-sm text-gray-500">
                                    Lot No:{" "}
                                    <span className="font-semibold text-blue-700">
                                        {selectedLot?.lotNo || "-"}
                                    </span>
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={closeDetailModal}
                                title="Close"
                                className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-200 hover:text-gray-800"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {selectedLot && (
                            <div className="grid grid-cols-2 gap-3 border-b border-gray-200 bg-white p-5 sm:grid-cols-4">
                                <div className="rounded-lg bg-gray-50 p-3">
                                    <p className="text-xs text-gray-500">
                                        Factory
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-gray-800">
                                        {selectedLot.factory || "-"}
                                    </p>
                                </div>

                                <div className="rounded-lg bg-gray-50 p-3">
                                    <p className="text-xs text-gray-500">
                                        Item Name
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-gray-800">
                                        {selectedLot.itemName || "-"}
                                    </p>
                                </div>

                                <div className="rounded-lg bg-gray-50 p-3">
                                    <p className="text-xs text-gray-500">
                                        In Quantity
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-green-700">
                                        {Number(selectedLot.inQty || 0).toLocaleString(
                                            "en-IN"
                                        )}
                                    </p>
                                </div>

                                <div className="rounded-lg bg-gray-50 p-3">
                                    <p className="text-xs text-gray-500">
                                        Current Balance
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-blue-700">
                                        {Number(
                                            selectedLot.balanceQty || 0
                                        ).toLocaleString("en-IN")}
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="max-h-[55vh] overflow-auto p-5">
                            {detailLoading ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <div className="h-9 w-9 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />

                                    <p className="mt-3 text-sm text-gray-500">
                                        Loading lot detail records...
                                    </p>
                                </div>
                            ) : detailError ? (
                                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center text-sm text-red-600">
                                    {detailError}
                                </div>
                            ) : (
                                <>

                                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                                        <table className="w-full min-w-[750px] text-left text-sm">
                                            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                                                <tr>
                                                    <th className="whitespace-nowrap px-4 py-3">
                                                        S.No.
                                                    </th>

                                                    <th className="whitespace-nowrap px-4 py-3">
                                                        Reference Type
                                                    </th>

                                                    <th className="whitespace-nowrap px-4 py-3">
                                                        Document No.
                                                    </th>

                                                    <th className="whitespace-nowrap px-4 py-3">
                                                        Document Date
                                                    </th>

                                                    <th className="whitespace-nowrap px-4 py-3 text-right">
                                                        Out Qty
                                                    </th>

                                                    <th className="whitespace-nowrap px-4 py-3 text-right">
                                                        Out Amount
                                                    </th>
                                                </tr>
                                            </thead>

                                            <tbody className="divide-y divide-gray-100">
                                                {lotDetails.length > 0 ? (
                                                    lotDetails.map((detail, index) => (
                                                        <tr
                                                            key={detail._id}
                                                            className="transition hover:bg-gray-50"
                                                        >
                                                            <td className="px-4 py-3 text-gray-600">
                                                                {index + 1}
                                                            </td>

                                                            <td className="px-4 py-3">
                                                                <span className="rounded-full bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-700">
                                                                    {detail.ReferenceType || "-"}
                                                                </span>
                                                            </td>

                                                            <td className="whitespace-nowrap px-4 py-3 font-medium text-blue-700">
                                                                {detail.DocumentNo || "-"}
                                                            </td>

                                                            <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                                                                {formatDate(
                                                                    detail.DocumentDate
                                                                )}
                                                            </td>

                                                            <td className="px-4 py-3 text-right font-medium text-red-600">
                                                                {Number(
                                                                    detail.OutQty || 0
                                                                ).toLocaleString("en-IN")}
                                                            </td>

                                                            <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-gray-800">
                                                                {formatAmount(
                                                                    detail.OutAmt
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td
                                                            colSpan={6}
                                                            className="px-4 py-12 text-center text-gray-500"
                                                        >
                                                            No detail records found for
                                                            this lot.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>

                                            {lotDetails.length > 0 && (
                                                <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                                                    <tr>
                                                        <td
                                                            colSpan={4}
                                                            className="px-4 py-3 text-right font-bold text-gray-700"
                                                        >
                                                            Total
                                                        </td>

                                                        <td className="px-4 py-3 text-right font-bold text-red-600">
                                                            {lotDetails
                                                                .reduce(
                                                                    (total, detail) =>
                                                                        total +
                                                                        Number(
                                                                            detail.OutQty || 0
                                                                        ),
                                                                    0
                                                                )
                                                                .toLocaleString("en-IN")}
                                                        </td>

                                                        <td className="whitespace-nowrap px-4 py-3 text-right font-bold text-gray-800">
                                                            {formatAmount(
                                                                lotDetails.reduce(
                                                                    (total, detail) =>
                                                                        total +
                                                                        Number(
                                                                            detail.OutAmt || 0
                                                                        ),
                                                                    0
                                                                )
                                                            )}
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>


                    </div>
                </div>
            )}


        </div>
    );
};

export default LotSummaryRecord;